import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface SessionDataPoint {
  date: string;
  value: number;
}

interface SessionTrendsData {
  sessions: SessionDataPoint[];
}

interface SessionTrendsProps {
  data: SessionTrendsData | null;
  timeRange?: string;
}

export function SessionTrends({ data }: SessionTrendsProps) {
  if (!data?.sessions) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">No session data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {/* Daily Sessions */}
        <Card>
          <CardHeader>
            <CardTitle>Daily Session Count</CardTitle>
            <CardDescription>
              Number of training sessions per day
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.sessions}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => [value, 'Sessions']}
                />
                <Bar 
                  dataKey="value" 
                  fill="hsl(var(--primary))"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Session Trend Line */}
        <Card>
          <CardHeader>
            <CardTitle>Session Trend</CardTitle>
            <CardDescription>
              Training session frequency over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.sessions}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => [value, 'Sessions']}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="hsl(var(--secondary))"
                  strokeWidth={3}
                  dot={{ fill: 'hsl(var(--secondary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Session Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Session Statistics</CardTitle>
          <CardDescription>
            Detailed breakdown of training session metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {data.sessions.reduce((sum: number, day: SessionDataPoint) => sum + day.value, 0)}
              </div>
              <p className="text-sm text-muted-foreground">Total Sessions</p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {Math.round(data.sessions.reduce((sum: number, day: SessionDataPoint) => sum + day.value, 0) / data.sessions.length)}
              </div>
              <p className="text-sm text-muted-foreground">Daily Average</p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {Math.max(...data.sessions.map((day: SessionDataPoint) => day.value))}
              </div>
              <p className="text-sm text-muted-foreground">Peak Day</p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {data.sessions.filter((day: SessionDataPoint) => day.value > 0).length}
              </div>
              <p className="text-sm text-muted-foreground">Active Days</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}